import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Seo, LOCAL_BUSINESS_JSONLD } from '../components/Seo';
import { usePageContent } from '../hooks/usePageContent';
import { publicApi } from '../lib/api';
import { SERVICE_OVERVIEW, BOOKING_STEPS, WHY_CHOOSE } from '../lib/site';

const HERO_BG = 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=1920';

const Home = () => {
  const { t } = usePageContent('home');
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    publicApi.getEvents().then((d) => setEvents((d || []).slice(0, 3))).catch(() => {});
    publicApi.getTestimonials().then((d) => setTestimonials(d || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#1F1A17]">
      <Seo
        exactTitle="Mayden Films | Photography, Videography & Glam Booth"
        title="Glam Booth Hire Cranbourne & Southeast Melbourne"
        description="Mayden Films brings a premium Glam Booth experience to weddings, birthdays, formals and corporate events across Cranbourne, the City of Casey and Southeast Melbourne."
        path="/"
        jsonLd={LOCAL_BUSINESS_JSONLD}
      />

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={t('hero_image', HERO_BG)} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F1A17] via-[#1F1A17]/85 to-[#1F1A17]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="text-[#C5A880] tracking-[0.25em] uppercase text-xs sm:text-sm font-semibold mb-5">{t('hero_eyebrow', 'Glam Booth · Photography · Videography')}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EADBC8] leading-tight mb-6">
              {t('hero_title', 'Cinematic memories, beautifully captured.')}
            </h1>
            <p className="text-base md:text-lg text-[#EADBC8]/70 mb-9 leading-relaxed">
              {t('hero_subtitle', 'Mayden Films brings a premium Glam Booth, event photography and cinematic videography to weddings, birthdays, debuts, christenings and corporate celebrations across Cranbourne and Southeast Melbourne.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/quote">
                <Button className="w-full sm:w-auto bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-8 py-6 text-base transition-transform duration-300 hover:scale-105" data-testid="hero-primary-cta">
                  {t('hero_button_primary', 'Check Your Event Date')}
                </Button>
              </Link>
              <Link to="/glam-booth">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-[#C5A880] text-[#C5A880] hover:bg-[#C5A880] hover:text-[#1F1A17] font-semibold px-8 py-6 text-base transition-colors duration-300" data-testid="hero-secondary-cta">
                  {t('hero_button_secondary', 'View the Glam Booth')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Short intro */}
      <section className="py-20 bg-[#1A1612]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-6">{t('intro_title', 'Beautifully crafted, personally delivered')}</h2>
          <p className="text-[#EADBC8]/70 text-base md:text-lg leading-relaxed">
            {t('intro_paragraph', 'Every Mayden Films booking is prepared and run personally by Darwin — with professional equipment, thoughtful lighting, personalised design and instant digital sharing. Whether it\u2019s our interactive Glam Booth, considered event photography or a cinematic highlight film, the goal is the same: relaxed, enjoyable coverage that looks beautiful and keeps your guests smiling.')}
          </p>
        </div>
      </section>

      {/* Services overview */}
      <section className="py-20 bg-[#1F1A17]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-[#C5A880] tracking-widest uppercase text-xs font-semibold mb-3">{t('services_eyebrow', 'What we offer')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-3">{t('services_title', 'Four ways to capture your celebration')}</h2>
            <p className="text-[#EADBC8]/60 max-w-2xl mx-auto">{t('services_subtitle', 'From an interactive Glam Booth to cinematic film — choose one, or combine them for a complete experience.')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICE_OVERVIEW.map((s) => {
              const Icon = LucideIcons[s.icon] || LucideIcons.Star;
              return (
                <Link key={s.name} to={s.to} data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="group">
                  <Card className="bg-[#2A2420] border-[#C5A880]/20 hover:border-[#C5A880]/60 transition-colors h-full">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="w-12 h-12 rounded-full bg-[#C5A880]/10 flex items-center justify-center mb-4 group-hover:bg-[#C5A880]/20 transition-colors">
                        <Icon className="text-[#C5A880]" size={22} />
                      </div>
                      <h3 className="text-[#EADBC8] font-semibold text-lg mb-1">{s.name}</h3>
                      <p className="text-[#C5A880] text-sm font-medium mb-3">{s.price}</p>
                      <p className="text-[#EADBC8]/55 text-sm flex-1">{s.description}</p>
                      <span className="text-[#C5A880] text-sm font-medium mt-4 flex items-center gap-1.5 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={15} /></span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-10 py-6 text-base transition-transform duration-300 hover:scale-105">Request a Quote</Button></Link>
          </div>
        </div>
      </section>

      {/* How booking works */}
      <section className="py-20 bg-[#1A1612]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-3">{t('booking_title', 'How booking works')}</h2>
            <p className="text-[#EADBC8]/60">{t('booking_subtitle', 'A simple, transparent process from enquiry to gallery.')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BOOKING_STEPS.map((s) => (
              <Card key={s.step} className="bg-[#2A2420] border-[#C5A880]/20 hover:border-[#C5A880]/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-full bg-[#C5A880] text-[#1F1A17] font-bold flex items-center justify-center mb-4">{s.step}</div>
                  <h3 className="text-[#EADBC8] font-semibold mb-1.5 text-base">{s.title}</h3>
                  <p className="text-[#EADBC8]/55 text-sm">{s.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Previous events preview */}
      {events.length > 0 && (
        <section className="py-20 bg-[#1F1A17]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-2">{t('recent_title', 'Recent events')}</h2>
                <p className="text-[#EADBC8]/60">{t('recent_subtitle', 'A glimpse of recent celebrations.')}</p>
              </div>
              <Link to="/previous-events" className="text-[#C5A880] hover:text-[#D4BC95] font-medium flex items-center gap-2">
                See all events <ArrowRight size={18} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((ev) => (
                <Link key={ev.id} to="/previous-events" className="group">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#2A2420] relative">
                    {ev.hero_image ? (
                      <img src={ev.hero_image} alt={ev.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#EADBC8]/30">{ev.title}</div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1F1A17]/80 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <p className="text-[#EADBC8] font-semibold">{ev.title}</p>
                      {ev.event_type && <p className="text-[#C5A880] text-xs">{ev.event_type}</p>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why choose */}
      <section className="py-20 bg-[#1A1612]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-3">{t('why_title', 'Why choose Mayden Films')}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_CHOOSE.map((item) => {
              const Icon = LucideIcons[item.icon] || LucideIcons.Star;
              return (
                <Card key={item.title} className="bg-[#2A2420] border-[#C5A880]/20 hover:border-[#C5A880]/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-full bg-[#C5A880]/10 flex items-center justify-center mb-4">
                      <Icon className="text-[#C5A880]" size={22} />
                    </div>
                    <h3 className="text-[#EADBC8] font-semibold mb-1.5">{item.title}</h3>
                    <p className="text-[#EADBC8]/55 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials — only genuine, published reviews */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-[#1A1612]" data-testid="home-testimonials">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#C5A880] tracking-widest uppercase text-xs font-semibold mb-3">Kind words</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8]">What clients say</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((tm) => (
                <Card key={tm.id} className="bg-[#2A2420] border-[#C5A880]/20" data-testid={`testimonial-${tm.id}`}>
                  <CardContent className="p-6">
                    <div className="flex mb-3">
                      {[...Array(tm.rating || 5)].map((_, i) => {
                        const Star = LucideIcons.Star;
                        return <Star key={i} size={16} className="text-[#C5A880] fill-[#C5A880]" />;
                      })}
                    </div>
                    <p className="text-[#EADBC8]/85 text-sm italic leading-relaxed mb-4">&ldquo;{tm.text}&rdquo;</p>
                    <p className="text-[#EADBC8] font-semibold text-sm">{tm.name}</p>
                    <p className="text-[#C5A880] text-xs">{tm.event}{tm.date ? ` · ${tm.date}` : ''}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#2A2420] via-[#1F1A17] to-[#2A2420]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-5">{t('cta_title', 'Planning an event?')}</h2>
          <p className="text-[#EADBC8]/70 text-lg mb-8">
            {t('cta_subtitle', "Tell me the date, venue and celebration, and I'll confirm availability.")}
          </p>
          <Link to="/quote">
            <Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-10 py-6 text-base transition-transform duration-300 hover:scale-105">
              {t('cta_button', 'Check Your Event Date')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
